import React from "react";
import { motion } from "framer-motion";

const Packages = () => {
  const packagesData = [
    {
      id: "solitude",
      tag: "SOLITUDE",
      title: "Cinematic Portrait Experience",
      price: "LKR 12,000 – 20,000",
      includes: [
        "concept planning",
        "moodboard discussion",
        "location guidance",
        "1–2 hour session",
        "cinematic color grading",
        "8–12 edited images",
      ],
      additional: [
        "transport",
        "makeup artist",
        "wardrobe rental",
        "location/studio fees",
        "lighting rentals if required.",
      ],
    },
    {
      id: "still",
      tag: "STILL",
      title: "Cinematic Couple Storytelling",
      price: "LKR 20,000 – 40,000",
      includes: [
        "concept planning",
        "couple direction",
        "location scouting",
        "cinematic photography session",
        "12–18 edited images",
        "consistent cinematic grading",
      ],
      additional: [
        "transport",
        "dresses/outfits",
        "makeup artist",
        "flower props",
        "fog/light rentals",
        "studio/location permits",
      ],
    },
    {
      id: "ceremony",
      tag: "CEREMONY",
      title: "Cinematic Wedding Coverage",
      subtitle: "for the newly wed couple",
      price: "LKR 45,000 – 120,000",
      context:
        "Current Realistic Pricing. Depends on: hours, locations, guest size, deliverables, assistant needs.",
      includes: [
        "consultation/planning",
        "cinematic wedding portraiture",
        "ceremony coverage",
        "curated edited gallery",
        "cinematic color treatment",
        "selected hero retouching",
      ],
      additional: [
        "second photographer",
        "transport",
        "accommodation",
        "lighting rentals",
        "albums/prints",
      ],
      insight: {
        heading: "Why wedding pricing is higher:",
        text: "Because weddings involve: pressure, physical exhaustion, backup responsibility, long editing hours, no retakes, equipment risk.",
      },
    },
    {
      id: "noir",
      tag: "NOIR",
      title: "Editorial Fashion Storytelling",
      price: "LKR 15,000 – 35,000",
      context:
        "Why fashion pricing varies: Fashion work is production heavy, styling dependent, creatively demanding.",
      includes: [
        "moodboard development",
        "styling direction",
        "location planning",
        "cinematic editorial photography",
        "8–12 edited images",
      ],
      additional: [
        "stylists",
        "MUAs",
        "wardrobe rental",
        "studio rental",
        "props/lighting",
      ],
    },
  ];

  return (
    <section className="bg-dreamy-cream py-24 px-6 md:px-20 text-dreamy-earth overflow-hidden">
      <div className="max-w-6xl mx-auto space-y-16">
        {/* SECTION HEADER */}
        <div className="text-center space-y-4">
          <p className="font-sans text-[11px] tracking-[0.4em] uppercase text-dreamy-earth/50">
            Investment
          </p>
          <h2 className="font-serif text-5xl md:text-6xl italic text-dreamy-earth">
            The <span className="text-dreamy-gold not-italic">Packages</span>
          </h2>
          <div className="w-12 h-[1px] bg-dreamy-gold mx-auto mt-6" />
        </div>

        {/* PACKAGES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          {packagesData.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              className="bg-[#FFFDF9] border border-dreamy-earth/5 rounded-[30px] p-8 md:p-10 shadow-sm hover:shadow-xl hover:border-dreamy-gold/30 transition-all duration-500 flex flex-col justify-between h-full"
            >
              <div className="space-y-6">
                {/* Header info */}
                <div className="flex justify-between items-baseline gap-4 border-b border-dreamy-gold/20 pb-4">
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-dreamy-gold font-medium">
                      {pkg.tag}
                    </span>
                    <h3 className="font-serif text-2xl text-dreamy-earth mt-1">
                      {pkg.title}
                    </h3>
                    {pkg.subtitle && (
                      <p className="font-sans text-xs italic text-dreamy-earth/60 mt-0.5">
                        ({pkg.subtitle})
                      </p>
                    )}
                  </div>
                </div>

                {/* Price tag */}
                <div className="py-2">
                  <p className="font-sans text-sm tracking-wider text-dreamy-earth/50 uppercase text-[10px]">
                    Investment Range
                  </p>
                  <p className="font-serif text-2xl text-dreamy-earth font-medium tracking-tight mt-1">
                    {pkg.price}
                  </p>
                  {pkg.context && (
                    <p className="font-sans text-[11px] leading-relaxed text-dreamy-earth/60 mt-2 bg-dreamy-cream/50 p-3 rounded-xl border border-dreamy-earth/5">
                      {pkg.context}
                    </p>
                  )}
                </div>

                {/* Includes Section */}
                <div className="space-y-3">
                  <h4 className="font-sans text-[11px] tracking-[0.2em] uppercase text-dreamy-earth/80 font-semibold">
                    Includes:
                  </h4>
                  <ul className="space-y-2 font-sans text-sm text-dreamy-earth/70 capitalize">
                    {pkg.includes.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-dreamy-gold flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Additional Costs Section */}
                <div className="space-y-3 pt-4 border-t border-dreamy-earth/5">
                  <h4 className="font-sans text-[11px] tracking-[0.2em] uppercase text-dreamy-earth/40 font-semibold">
                    Additional Costs:
                  </h4>
                  <ul className="space-y-1.5 font-sans text-xs text-dreamy-earth/50 capitalize">
                    {pkg.additional.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="mt-1.5 text-[8px] text-dreamy-earth/30">
                          •
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Special Context Insights (For Weddings) */}
              {pkg.insight && (
                <div className="mt-8 p-4 rounded-2xl bg-dreamy-earth/[0.02] border border-dreamy-gold/10 space-y-1">
                  <h5 className="font-sans text-[10px] tracking-wider uppercase text-dreamy-gold font-semibold">
                    {pkg.insight.heading}
                  </h5>
                  <p className="font-sans text-xs text-dreamy-earth/60 leading-relaxed">
                    {pkg.insight.text}
                  </p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Packages;
